import { createLogger } from './logger';
import { FALLBACK_ICE_SERVERS } from './iceServers';

/**
 * PeerSession — one WebRTC connection for one match.
 *
 * Design: single negotiation, ever.
 * - Audio + video tracks are BOTH added before the one and only offer/answer.
 * - Reveal/Hide never touches this class (UI + signaling concern only).
 * - Roles are server-assigned: exactly one offerer, one answerer. No glare.
 * - ICE candidates arriving before the remote description are queued.
 * - ICE servers (STUN/TURN) are injected by the caller so short-lived
 *   Cloudflare TURN credentials can be used without changing this class.
 */
export class PeerSession {
  constructor({ role, localStream, sendSignal, onRemoteStream, onConnected, onFailed, iceServers }) {
    this.log = createLogger(`peer:${role}`);
    this.role = role;
    this.sendSignal = sendSignal;
    this.onConnected = onConnected;
    this.onFailed = onFailed;
    this.closed = false;
    this.pendingCandidates = [];
    this.remoteStream = new MediaStream();

    const effectiveIceServers =
      Array.isArray(iceServers) && iceServers.length > 0 ? iceServers : FALLBACK_ICE_SERVERS;
    this.log('ice-config-applied', { serverCount: effectiveIceServers.length });
    const pc = new RTCPeerConnection({ iceServers: effectiveIceServers });
    this.pc = pc;

    localStream.getTracks().forEach((track) => {
      pc.addTrack(track, localStream);
      this.log('local-track-added', { kind: track.kind, enabled: track.enabled });
    });

    pc.ontrack = (event) => {
      this.log('remote-track-received', {
        kind: event.track.kind,
        muted: event.track.muted,
        readyState: event.track.readyState,
      });
      this.remoteStream.addTrack(event.track);
      onRemoteStream(this.remoteStream);
    };

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        // Diagnostic: log the candidate TYPE only (host/srflx/prflx/relay),
        // never the raw candidate string or credentials.
        this.log('local-ice-candidate', { type: event.candidate.type });
        this.sendSignal({ type: 'candidate', candidate: event.candidate.toJSON() });
      }
    };

    pc.oniceconnectionstatechange = () => {
      this.log('ice-state', { state: pc.iceConnectionState });
    };

    pc.onconnectionstatechange = () => {
      this.log('connection-state', { state: pc.connectionState });
      if (this.closed) return;
      if (pc.connectionState === 'connected') {
        this.onConnected();
      } else if (pc.connectionState === 'failed') {
        this.onFailed();
      }
    };

    // Single-negotiation architecture: renegotiation is never needed and never performed.
    pc.onnegotiationneeded = () => {
      this.log('negotiationneeded-ignored-by-design');
    };
  }

  async start() {
    if (this.role !== 'offerer' || this.closed) return;
    try {
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      this.log('offer-created', {
        hasAudio: offer.sdp.includes('m=audio'),
        hasVideo: offer.sdp.includes('m=video'),
      });
      this.sendSignal(this.pc.localDescription.toJSON());
    } catch (err) {
      this.log('offer-error', { error: err.message });
      this.onFailed();
    }
  }

  async handleSignal(signal) {
    if (this.closed) return;
    try {
      if (signal.type === 'offer') {
        if (this.role !== 'answerer') {
          this.log('offer-ignored', { reason: 'not-answerer' });
          return;
        }
        if (this.pc.signalingState !== 'stable') {
          this.log('offer-ignored', { reason: 'already-negotiating', state: this.pc.signalingState });
          return;
        }
        await this.pc.setRemoteDescription(signal);
        this.log('offer-applied');
        await this.flushCandidates();
        const answer = await this.pc.createAnswer();
        await this.pc.setLocalDescription(answer);
        this.log('answer-created', {
          hasAudio: answer.sdp.includes('m=audio'),
          hasVideo: answer.sdp.includes('m=video'),
        });
        this.sendSignal(this.pc.localDescription.toJSON());
      } else if (signal.type === 'answer') {
        if (this.pc.signalingState !== 'have-local-offer') {
          this.log('answer-ignored', { state: this.pc.signalingState });
          return;
        }
        await this.pc.setRemoteDescription(signal);
        this.log('answer-applied');
        await this.flushCandidates();
      } else if (signal.type === 'candidate' && signal.candidate) {
        if (this.pc.remoteDescription) {
          await this.pc.addIceCandidate(signal.candidate);
        } else {
          this.pendingCandidates.push(signal.candidate);
          this.log('candidate-queued', { queued: this.pendingCandidates.length });
        }
      }
    } catch (err) {
      this.log('signal-error', { type: signal.type, error: err.message });
    }
  }

  async flushCandidates() {
    const queued = this.pendingCandidates.splice(0);
    for (const c of queued) {
      try {
        await this.pc.addIceCandidate(c);
      } catch (err) {
        this.log('candidate-flush-error', { error: err.message });
      }
    }
    if (queued.length) this.log('candidates-flushed', { count: queued.length });
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    this.pc.ontrack = null;
    this.pc.onicecandidate = null;
    this.pc.oniceconnectionstatechange = null;
    this.pc.onconnectionstatechange = null;
    this.pc.onnegotiationneeded = null;
    this.pc.close();
    this.log('closed');
  }
}
